import { Fragment, useCallback } from 'react';
import { useFormikContext } from 'formik';
import { deletePanel, updatePanelSize } from '../common/panel-grid';
import { type PageCmsPanels, type PanelSize } from '../common/panel-types';
import { PanelGrid } from '../CustomPage.styled';
import { PanelGridRow } from './PanelGridRow';
import { PanelGridRowDropZone } from './PanelGridRowDropZone';

interface PanelGridFormValues {
  panels: PageCmsPanels;
}

const EMPTY_PANELS: PageCmsPanels = [];

export const PanelGridLayout = () => {
  const { values, setFieldValue } = useFormikContext<PanelGridFormValues>();
  const panels = values.panels ?? EMPTY_PANELS;

  // Formik's setFieldValue is referentially stable, so these callbacks only change identity
  // when `panels` changes — keeping the memoized rows below from re-rendering on unrelated
  // form updates (e.g. typing the page title).
  const handleSizeChange = useCallback(
    (rowIndex: number, panelId: string, size: PanelSize) =>
      setFieldValue('panels', updatePanelSize(panels, rowIndex, panelId, size)),
    [panels, setFieldValue]
  );

  const handleDeletePanel = useCallback(
    (rowIndex: number, panelId: string) => setFieldValue('panels', deletePanel(panels, rowIndex, panelId)),
    [panels, setFieldValue]
  );

  return (
    <PanelGrid data-testid="panel-grid-layout">
      <PanelGridRowDropZone rowIndex={0} />
      {panels.map((row, rowIndex) => (
        // Rows have no id of their own; keying by the first panel is stable for display and
        // size/delete edits — revisit when drag-and-drop reordering lands.
        <Fragment key={row[0]?.id ?? rowIndex}>
          <PanelGridRow
            row={row}
            rowIndex={rowIndex}
            onSizeChange={handleSizeChange}
            onDeletePanel={handleDeletePanel}
          />
          <PanelGridRowDropZone rowIndex={rowIndex + 1} />
        </Fragment>
      ))}
    </PanelGrid>
  );
};
